// docs/116 the cozy inn: the inn's own layer on the village view's stage. The view paints the room and
// draws the band and the village's folk; this layer adds what is the inn's: the places a band can go
// (the hearth, an armchair, a window, the noticeboard, the guestbook, the bard, the bar, the dice table,
// the stairs), each a hotspot that walks my band there and starts what it does there (CI3); the inn's
// life (`life/InnLife`: the crowd, the bard and the storyteller, the serving hands, the candles, the
// cat and the dog, the season's dressing); and the dice on the dice table (`RoomDice`), with the one
// `DiceDirector` that sounds the game. Panels over the view are `InnOverlays`.
import { useEffect, useRef, type CSSProperties } from 'react';
import { playSfx } from '../../../../core/audio';
import type { Station } from '../layout';
import { nearestFree } from '../walk';
import { poseStations, watchStations } from './stations';
import { useInn, type InnPanel } from './store';
import { POSE, type PoseCode } from './types';
import type { InnStage } from './stage';
import { InnLife } from './life/InnLife';
import { DiceDirector } from './DiceDirector';
import { RoomDice } from './RoomDice';
import { bookFor } from './books';
import './inn.css';

interface Spot { key: string; name: string; sub: string; x: number; y: number; w: number; h: number; go(): void }

export function InnLayer({ stage }: { stage: InnStage }): React.JSX.Element {
  const { plan, room, mine, data } = stage;
  const open = useInn((s) => s.open);
  const setPose = useInn((s) => s.setPose);
  const act = useInn((s) => s.act);
  const keys = mine.map((h) => h.key);
  const people = mine.filter((h) => !h.companion).map((h) => h.key);
  const mySeat = data.dice?.seats.findIndex((s) => s.mine) ?? -1;

  /** Walk my band to the stations of `pose`, and once there do `then`; if a seat on the way was taken first, `again` (docs/120 VM4). */
  const go = (pose: PoseCode, stations: Station[], then?: () => void, again?: () => void) => {
    act(keys, undefined);
    useInn.getState().setBook(undefined);
    setPose(pose);
    stage.walkBand(stations, pose, then, again);
  };
  /** docs/120 VM4: someone else is in it: say so, and stay put. */
  const busy = (what: string) => { stage.note?.(what); playSfx('ui.error', { gain: 0.5 }); };
  const others = () => stage.occupied(keys.map((k) => `h:${k}`));
  const at = (pose: PoseCode, opts: { seat?: number; window?: number } = {}) => poseStations(plan, pose, mine.length, others(), opts);
  /** Stand the band near a thing on the floor (a station in front of it, the rest filed beside). */
  const before = (st: Station): Station[] => mine.map((_, i) => nearestFree(stage.grid, { x: st.x - i * 0.8, y: st.y + (i % 2) * 0.3 }));
  const openAfter = (panel: InnPanel, opts?: Parameters<typeof open>[1]) => () => { open(panel, opts); playSfx('ui.open'); };

  const spots: Spot[] = stage.upstairs ? plan.beds.map((b, i) => ({
    key: `bed-${i}`, name: 'A cosy bed', sub: 'sleep until morning', x: b.x - 0.1, y: b.y - 0.6, w: 2.2, h: 1.5,
    go: function bed() {
      // docs/120 VM4: each sleeper to a bed nobody else is in, this one first
      const beds = [b.sleeper, ...plan.beds.map((x) => x.sleeper).filter((x) => x !== b.sleeper)].filter((x) => stage.free(x));
      if (!stage.free(b.sleeper)) { busy('Someone is asleep in that bed.'); return; }
      go(POSE.asleep, beds.slice(0, people.length), () => act(people, 'sleep'), bed);
    },
  })) : [
    { key: 'hearth', name: 'The hearth', sub: 'gather round the fire', x: (room.hearth?.x ?? 0) - 0.2, y: room.floor - 2, w: 3.4, h: 2.4,
      go: function hearth() { go(POSE.hearth, at(POSE.hearth), () => act(people, 'warm'), hearth); } },
    ...plan.armchairs.map((c, i) => ({ key: `armchair-${i}`, name: 'An armchair', sub: 'settle in with a book', x: c.x - 0.15, y: c.y - 0.45, w: 1.3, h: 1.4,
      go: function chair() {
        if (!stage.free(c)) { busy('Someone is sitting in that armchair.'); return; }
        go(POSE.armchair, [c, ...at(POSE.hearth)], () => {
        const reader = people[0];
        if (!reader) return;
        act([reader], 'read');
        act(people.slice(1), 'warm');
        useInn.getState().setBook({ key: reader, title: bookFor(stage.look.seed, reader) });
        playSfx('inn.page', { gain: 0.6 });
      }, chair);
      } })),
    ...room.windows.map((w, i) => ({ key: `window-${i}`, name: 'A window', sub: 'gaze out at the weather', x: w.x - 0.1, y: w.y - 0.1, w: 1.2, h: w.h + 0.3,
      go: function gaze() { go(POSE.window, at(POSE.window, { window: i }), () => { act(people, 'gaze'); open('window', { window: i }); }, gaze); } })),
    { key: 'notice', name: 'The noticeboard', sub: 'notices, rumours, work', ...plan.notice,
      go: () => go(POSE.standing, before({ x: plan.notice.x + 0.5, y: room.floor + 1.05 }), openAfter('notice')) },
    { key: 'lectern', name: 'The guestbook', sub: 'read it, sign it', x: plan.lectern.x - 0.1, y: plan.lectern.y - 0.5, w: 1.2, h: 1.5,
      go: () => go(POSE.standing, before({ x: plan.lectern.x + 0.6, y: plan.lectern.y + 0.2 }), openAfter('guestbook')) },
    { key: 'bard', name: 'The bard', sub: 'ask for a song', x: plan.bard.x - 0.1, y: plan.bard.y - 0.3, w: 1.2, h: 1.3,
      go: () => go(POSE.standing, before({ x: plan.bard.x - 1, y: plan.bard.y + 0.4 }), openAfter('bard')) },
    { key: 'bar', name: 'The bar', sub: 'order a drink', x: plan.bar.x, y: plan.bar.y - 0.15, w: plan.bar.w, h: 1.1,
      go: function bar() { go(POSE.bar, at(POSE.bar), openAfter('bar'), bar); } },
    { key: 'dice', name: 'The dice table', sub: data.dice?.state === 'playing' ? 'Hearth Bones: a game is on' : 'Hearth Bones: sit in or watch', x: plan.dice.x - 1.2, y: plan.dice.y - 1.1, w: plan.dice.w + 2.4, h: 2.1,
      go: () => {
        const seated = mySeat >= 0;
        go(POSE.dice, seated ? at(POSE.dice, { seat: mySeat }) : [...watchStations(plan, mine.length, others())], openAfter('dice', { diceMode: seated ? 'seat' : 'watch' }));
      } },
    { key: 'stairs', name: 'The stairs', sub: data.rented ? 'up to your room' : 'rent a room upstairs', x: plan.stairs.x - 0.3, y: plan.stairs.top.y, w: 2.1, h: room.floor + 1.6 - plan.stairs.top.y,
      go: () => {
        if (!data.rented) { go(POSE.standing, before({ x: plan.stairs.x - 0.6, y: room.floor + 1.3 }), openAfter('room')); return; }
        go(POSE.standing, before({ x: plan.stairs.x, y: room.floor + 1.1 }), () => { playSfx('inn.stairs'); data.act.upstairs(true); });
      } },
  ];

  // the panels' buttons walk the band the same way as clicking the place itself
  const goRef = useRef(spots);
  goRef.current = spots;
  useEffect(() => { useInn.setState({ go: (key: string) => goRef.current.find((x) => x.key === key)?.go() }); return () => useInn.setState({ go: undefined }); }, []);
  // sitting down from the watching view (or getting up) swaps the close-up to my seat and walks the band to it
  const seatWas = useRef(mySeat);
  useEffect(() => {
    if (mySeat === seatWas.current) return;
    const sat = mySeat >= 0 && seatWas.current < 0, rose = mySeat < 0 && seatWas.current >= 0;
    seatWas.current = mySeat;
    if (useInn.getState().panel !== 'dice') return;
    if (sat) { useInn.setState({ diceMode: 'seat' }); go(POSE.dice, at(POSE.dice, { seat: mySeat })); }
    if (rose) useInn.setState({ diceMode: 'watch' });
  }, [mySeat]);
  const box = (s: Spot): CSSProperties => ({ left: Math.round(s.x * stage.cellPx), top: Math.round(s.y * stage.cellPx), width: Math.round(s.w * stage.cellPx), height: Math.round(s.h * stage.cellPx) });
  return (
    <>
      {spots.map((s) => (
        <button key={s.key} type="button" className="fk-hit fk-inn__spot" data-testid="inn-spot" data-spot={s.key} style={box(s)} aria-label={`${s.name}: ${s.sub}`}
          onClick={() => { playSfx('ui.click'); s.go(); }}>
          <span className="fk-village__plate fk-inn__plate"><span className="fk-village__plate-name">{s.name}</span><span className="fk-village__plate-sub">{s.sub}</span></span>
        </button>
      ))}
      <InnLife stage={stage} />
      {!stage.upstairs && data.dice && (
        <div className="fk-inn__roomdice" style={{ ...stage.at(plan.dice.x, plan.dice.y), zIndex: Math.round(plan.dice.y * 10) + 6 }}>
          <RoomDice view={data.dice} scale={stage.scale} cols={plan.dice.w} rows={1} stools={plan.dice.stools.map((st) => ({ x: Math.round((st.x - plan.dice.x + 0.5) * stage.cellPx), y: Math.round((st.y - plan.dice.y) * stage.cellPx) }))} />
        </div>
      )}
      {!stage.upstairs && data.dice && <DiceDirector view={data.dice} />}
    </>
  );
}
